import React, { useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { agents, api_s2, app, chatkitDomainKey } from "../../config";
import { useChatKitScript } from "../ai/chatkitScript";
import { applyChatKitHeaders, chatKitHeaders } from "../../utils/chatkitHeaders";
import { resolveChatKitRequest } from "../../utils/chatkitRequestUrl";
import { Invitee } from "../../hooks/useInviteePicker";
import "./eventManager.css";

interface EventAssistantPanelProps {
	/** Called when a slot card's button is pressed. Local-naive ISO strings. */
	onUseSlot: (start: string, end: string) => void;
	/** Called when an invitee card's button is pressed. */
	onAddInvitee: (invitee: Invitee) => void;
}

/**
 * The create flow's chat panel: describe the event, get people and times back.
 *
 * A host for the real ChatKit web component against our own `openai/chatkit/`
 * endpoint — our server, our auth, our agent. The agent has two function tools
 * (`search_invitees`, `find_available_times`, in pymble-apis
 * `services/event/tools.py`) and the server turns what they return into cards
 * with buttons; this component's whole job is auth, mounting, and handing those
 * button presses back to the create flow.
 *
 * ⚠️ EVERYTHING HERE IS A PROPOSAL. Pressing a card's button is what adds an
 * invitee or picks a time. Nothing is committed by the model, and that is
 * deliberate: an invitee set decides which students get taken out of a class,
 * and a chat message is not consent to that.
 *
 * WHY THE TIMES ARRIVE AS CARDS rather than in the reply. Tool output is
 * model-facing — the `/ai/` route logs it and never streams it — so a time that
 * reached the screen through the prose would be a number a language model had
 * retyped, and a transcription slip would be indistinguishable from a real
 * answer. The card is built server-side from the availability service's own
 * response, so the rule this feature rests on ("the model must not invent
 * slots") holds by construction rather than by prompting.
 *
 * ⚠️ PII. The transcript holds free text a staff member typed, which will
 * sometimes contain a student's name, and the cards carry names and year
 * levels. ChatKit's own state and our server's thread store hold it; nothing is
 * put in `localStorage` and nothing is logged.
 */
export const EventAssistantPanel: React.FC<EventAssistantPanelProps> = ({
	onUseSlot,
	onAddInvitee,
}) => {
	const { instance } = useMsal();
	const scriptState = useChatKitScript();

	/* Read through refs so `chatkitOptions` does not rebuild when the flow
	   re-renders. `useChatKit` calls `fetch` fresh for every request, so a ref is
	   always current by the time it matters — the pattern the portal's
	   ChatKitEmbed uses, and for the same reason. */
	const onUseSlotRef = useRef(onUseSlot);
	onUseSlotRef.current = onUseSlot;
	const onAddInviteeRef = useRef(onAddInvitee);
	onAddInviteeRef.current = onAddInvitee;

	const acquireToken = useCallback(async (): Promise<string> => {
		// MSAL v4 wants this before any call, and it is idempotent.
		await instance.initialize();
		const account = instance.getAllAccounts()[0];
		if (!account) throw new Error("No MSAL account");
		try {
			const auth = await instance.acquireTokenSilent({
				scopes: api_s2.scopes,
				account,
			});
			return auth.accessToken;
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				await instance.acquireTokenRedirect({
					scopes: api_s2.scopes,
					account,
				});
			}
			throw err;
		}
	}, [instance]);

	const chatkitFetch = useCallback(
		async (url: string | URL | Request, options?: RequestInit) => {
			/* Refuses any authenticated request whose resolved origin is not the
			   API's, and returns the ABSOLUTE url — `fetch` would otherwise
			   resolve a relative path against the document origin, which is a
			   different host in staging and production. See the module's own note:
			   the widget takes a `file_url` straight from the model, and an
			   absolute one pointing elsewhere turns "download this" into "send
			   this person's access token somewhere else". */
			const target = resolveChatKitRequest(url, api_s2.baseURL);
			const accessToken = await acquireToken();
			const headers = new Headers(options?.headers ?? {});
			applyChatKitHeaders(
				headers,
				chatKitHeaders({
					accessToken,
					agentId: agents.eventAssistant,
					/* No name or model: the builder omits them unless truthy, and
					   sending `model: ""` would blank the agent's configured model
					   server-side. */
					userContext: { currentPage: "event_create" },
					impersonateContactId: null,
				}),
				options?.body
			);
			return fetch(target, { ...options, headers });
		},
		[acquireToken]
	);

	const chatkitOptions = useMemo(
		() => ({
			api: {
				url: `${api_s2.baseURL}openai/chatkit/`,
				domainKey: chatkitDomainKey,
				fetch: chatkitFetch,
			},
			/* Both off: this is a side rail inside a modal, and a thread list or a
			   second header inside it would compete with the dialog's own. */
			history: { enabled: false },
			header: { enabled: false },
			theme: {
				color: { accent: { primary: app.colorPrimary, level: 2 as const } },
				radius: "round" as const,
				density: "compact" as const,
			},
			composer: { placeholder: "Ask AI to find a time" },
			startScreen: {
				greeting: "Tell me who is going and roughly when.",
			},
			threadItemActions: { feedback: false },
			widgets: {
				/**
				 * The card buttons. `type` and `payload` are set server-side in
				 * `chatkit/helpers/widgets.py` and must agree with it.
				 *
				 * Nothing is logged here, not even in development: the payload
				 * carries a person's name and id.
				 */
				onAction: async (action: {
					type: string;
					payload?: Record<string, unknown>;
				}): Promise<void> => {
					const p = action?.payload ?? {};
					const str = (key: string) =>
						typeof p[key] === "string" ? (p[key] as string) : "";

					if (action?.type === "event_use_slot") {
						const start = str("start");
						const end = str("end");
						if (start && end) onUseSlotRef.current(start, end);
						return;
					}
					if (action?.type === "event_add_invitee") {
						const id = str("id");
						const name = str("name");
						if (!id || !name) return;
						onAddInviteeRef.current({
							id,
							name,
							detail: str("detail"),
							kind: str("kind") === "group" ? "group" : "contact",
						});
					}
				},
			},
		}),
		[chatkitFetch]
	);

	const { control } = useChatKit(chatkitOptions);

	if (!agents.eventAssistant) {
		return (
			<aside className="em-wiz__ai" aria-label="Scheduling assistant">
				<h4 className="em-wiz__ai-title">Chat with AI to find a time</h4>
				<p className="em-wiz__gated">
					The scheduling assistant isn&apos;t configured for this
					environment. Set the time yourself, or use &quot;Suggest
					times&quot; on the left.
				</p>
			</aside>
		);
	}

	return (
		<aside className="em-wiz__ai" aria-label="Scheduling assistant">
			<h4 className="em-wiz__ai-title">Chat with AI to find a time</h4>
			<p className="em-muted">
				I&apos;ll find who you name and check their calendars.
			</p>

			{/* ⚠️ NOT MOUNTED UNTIL THE SCRIPT IS READY. `@openai/chatkit` ships
			    type declarations and no JavaScript, so `<openai-chatkit>` is only
			    a real element once the CDN bundle has registered it. Rendered
			    early it is an unupgraded custom element: a box with no content,
			    no composer and NO ERROR, which reads as a broken chat rather
			    than an unloaded script. */}
			{scriptState === "ready" ? (
				<ChatKit control={control} className="em-wiz__chatkit" />
			) : (
				<p className="em-muted" role="status">
					{scriptState === "failed"
						? "The assistant could not be loaded. Set the time yourself, or use “Suggest times” on the left."
						: "Loading the assistant…"}
				</p>
			)}
		</aside>
	);
};
